import React, { useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { alpha, createTheme, ThemeProvider } from '@material-ui/core/styles';
import getTheme from 'theme';
import Link from '@material-ui/core/Link';
import ProgramGridByNiche from 'components/ProgramGridByNiche';
import LiveTvIcon from '@material-ui/icons/LiveTv';
import VideoLibraryIcon from '@material-ui/icons/VideoLibrary';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import DeviceHubIcon from '@material-ui/icons/DeviceHub';
import CampaignIcon from '@material-ui/icons/Campaign';
import VerifiedIcon from '@material-ui/icons/Verified';
import Timeline from '@material-ui/icons/Timeline';
import Storefront from '@material-ui/icons/Storefront';
import {
  STRATEGIC_NEON_CYAN,
  strategicOpening,
  businessLevelMetrics,
  impactAudienceMetrics,
  accreditedCommercialServices,
  tnoValueAdds,
  investmentRoadmap2026,
  strategicAlliesByCategory,
  strategicClosing,
} from './businessLevelPresentationData';

const BUSINESS_GOLD = '#c9a227';
/** Base “dark tech”: negro profundo; oro + cian como acentos. */
const BUSINESS_FALLBACK_BG = '#03040a';
const BUSINESS_PAGE_BG = [
  'radial-gradient(ellipse 108% 58% at 50% -4%, rgba(34,211,238,0.08) 0%, transparent 50%)',
  'radial-gradient(ellipse 78% 58% at 10% 94%, rgba(2,3,10,0.88) 0%, transparent 54%)',
  'linear-gradient(130deg, rgba(6,8,18,0.99) 0%, rgba(18,24,58,0.97) 42%, rgba(40,52,120,0.9) 76%, rgba(201,162,39,0.14) 100%)',
].join(', ');
const BG_PAPER = 'rgba(16, 20, 48, 0.96)';

function createBusinessDeckTheme(themeMode) {
  const base = getTheme(themeMode, 'blue');
  return createTheme(base, {
    palette: {
      ...base.palette,
      mode: 'dark',
      primary: {
        main: BUSINESS_GOLD,
        light: '#ffe082',
        dark: '#a67d12',
        contrastText: '#1a237e',
      },
      secondary: {
        main: '#7986cb',
        light: '#aab6fe',
        dark: '#49599a',
        contrastText: '#fff',
      },
      background: {
        default: '#0d1642',
        paper: BG_PAPER,
      },
      text: {
        primary: '#f8fafc',
        secondary: 'rgba(248, 250, 252, 0.82)',
        disabled: 'rgba(248, 250, 252, 0.45)',
      },
      divider: alpha(BUSINESS_GOLD, 0.28),
    },
    typography: {
      ...base.typography,
      fontSize: Math.round((base.typography?.fontSize ?? 14) * 1.1),
      body1: {
        ...base.typography.body1,
        fontSize: '1.06rem',
        lineHeight: 1.72,
      },
      body2: {
        ...base.typography.body2,
        fontSize: '0.98rem',
        lineHeight: 1.62,
      },
      overline: {
        ...base.typography.overline,
        fontSize: '0.82rem',
        lineHeight: 1.5,
      },
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          root: { color: 'inherit' },
        },
      },
    },
  });
}

const BrandTnoCon = ({ sxTno = {}, sxCon = {} }) => (
  <Box component="span" sx={{ whiteSpace: 'nowrap', display: 'inline' }}>
    <Box
      component="span"
      sx={{
        fontWeight: 900,
        letterSpacing: '-0.07em',
        color: 'common.white',
        textShadow:
          '0.02em 0 0 currentColor, -0.02em 0 0 currentColor, 0 0.02em 0 currentColor, 0 -0.02em 0 currentColor',
        ...sxTno,
      }}
    >
      TNO
    </Box>
    <Box
      component="span"
      sx={{
        fontWeight: 200,
        letterSpacing: '-0.07em',
        marginLeft: '-0.06em',
        color: 'rgba(255,255,255,0.72)',
        ...sxCon,
      }}
    >
      CON
    </Box>
  </Box>
);

const TNO_NETWORK_LOGO_WHITE = '/images/logos/logo_white.PNG';
const ADS_MEDIA_KIT_URL = 'https://ads.tnonetwork.com';
const BUSINESS_LEVEL_REGISTER = '/business-level';
function imgSrc(path) {
  if (!path) return '';
  const raw = typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL ? process.env.PUBLIC_URL : '';
  if (!raw || raw === '/') return path;
  const base = raw.replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
}

const sectionDividerSx = {
  borderBottom: '1px solid',
  borderColor: (theme) => alpha(theme.palette.primary.main, 0.22),
};

const heroSectionSx = {
  ...sectionDividerSx,
  borderBottom: `4px solid ${BUSINESS_GOLD}`,
  minHeight: { xs: '82vh', md: '86vh' },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  px: { xs: 2.5, sm: 4, md: 6 },
  py: { xs: 6, md: 8 },
  boxSizing: 'border-box',
  textAlign: 'center',
};

const sectionShellSx = {
  ...sectionDividerSx,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  px: { xs: 2.5, sm: 4, md: 6 },
  py: { xs: 6, md: 9 },
  boxSizing: 'border-box',
};

function brandMonogram(name) {
  const cleaned = String(name || '')
    .replace(/[\u2014\u2013].*$/, '')
    .replace(/[^A-Za-z0-9\u00C0-\u024F\s]/g, ' ')
    .trim();
  const words = cleaned.split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return `${words[0][0]}${words[1][0]}`.toUpperCase();
}

function BusinessLevelPresentation({ themeMode = 'light' }) {
  const deckTheme = useMemo(() => createBusinessDeckTheme(themeMode), [themeMode]);

  return (
    <ThemeProvider theme={deckTheme}>
      <Box
        sx={{
          position: 'relative',
          minHeight: '100vh',
          overflow: 'auto',
          overflowX: 'hidden',
          color: 'text.primary',
          bgcolor: BUSINESS_FALLBACK_BG,
          backgroundImage: BUSINESS_PAGE_BG,
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: { xs: 'scroll', md: 'fixed' },
          '&:before': {
            content: '" "',
            position: 'fixed',
            top: { xs: -100, md: -120 },
            right: { xs: -100, md: -120 },
            width: { xs: 280, md: 360 },
            height: { xs: 280, md: 360 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,214,120,0.5), rgba(201,162,39,0.28) 38%, rgba(201,162,39,0))',
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&:after': {
            content: '" "',
            position: 'fixed',
            bottom: { xs: -80, md: -100 },
            left: { xs: -60, md: -80 },
            width: { xs: 240, md: 300 },
            height: { xs: 240, md: 300 },
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(201,162,39,0.22), rgba(201,162,39,0))',
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              pt: { xs: 2.5, md: 3.5 },
              pb: { xs: 0.5, md: 1 },
              px: 2,
            }}
          >
            <Box
              component="img"
              src={imgSrc(TNO_NETWORK_LOGO_WHITE)}
              alt="TNO Network"
              sx={{
                display: 'block',
                maxWidth: { xs: 200, sm: 260, md: 300 },
                width: '100%',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          </Box>

          {/* Hero · Slide 1: valor de la atención */}
          <Box sx={heroSectionSx}>
            <Typography
              variant="overline"
              sx={{
                color: 'primary.main',
                letterSpacing: { xs: 3, md: 5 },
                fontWeight: 800,
                fontSize: { xs: '0.78rem', md: '0.95rem' },
                mb: 1,
                display: 'block',
              }}
            >
              <BrandTnoCon /> 2026 · Día 3 · The Business Level
            </Typography>
            <Typography
              variant="overline"
              sx={{
                color: STRATEGIC_NEON_CYAN,
                letterSpacing: { xs: 2, md: 3 },
                fontWeight: 800,
                fontSize: { xs: '0.72rem', md: '0.82rem' },
                mb: 2,
                display: 'block',
                textShadow: '0 0 18px rgba(34,211,238,0.35)',
              }}
            >
              {strategicOpening.platformLabel}
            </Typography>
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.85rem', sm: '2.35rem', md: '3.05rem', lg: '3.45rem' },
                lineHeight: 1.15,
                color: 'common.white',
                maxWidth: 1050,
                mx: 'auto',
                textShadow: '0 0 1px rgba(255,255,255,0.08)',
              }}
            >
              {strategicOpening.headline}
            </Typography>
            <Typography
              sx={{
                mt: 2,
                color: STRATEGIC_NEON_CYAN,
                fontSize: { xs: '1.08rem', md: '1.22rem' },
                fontWeight: 700,
                letterSpacing: 0.4,
                maxWidth: 800,
                mx: 'auto',
              }}
            >
              {strategicOpening.subline}
            </Typography>
            <Typography
              sx={{
                mt: { xs: 2.5, md: 3 },
                color: 'rgba(255,255,255,0.88)',
                fontSize: { xs: '1.08rem', md: '1.22rem' },
                lineHeight: 1.75,
                maxWidth: 880,
                mx: 'auto',
              }}
            >
              {strategicOpening.body}
            </Typography>
            <Typography
              sx={{
                mt: { xs: 2.5, md: 3 },
                color: 'rgba(255,255,255,0.78)',
                fontSize: { xs: '1.05rem', md: '1.2rem' },
                fontWeight: 600,
              }}
            >
              Miércoles 22 de abril de 2026 · 10:00 a. m. · Las Mercedes, Caracas
            </Typography>
          </Box>

          {/* Slide 2 · Ecosistema híbrido */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch' }}>
            <CampaignIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1, alignSelf: 'center' }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: 'center' }}>
              Nuestro diferencial
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', md: '2.45rem' },
                color: 'common.white',
                mb: 1.5,
                textAlign: 'center',
                alignSelf: 'center',
                maxWidth: 920,
              }}
            >
              El ecosistema híbrido
            </Typography>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.86)',
                textAlign: 'center',
                maxWidth: 800,
                mx: 'auto',
                mb: { xs: 3, md: 4 },
                fontSize: { xs: '1.05rem', md: '1.12rem' },
                lineHeight: 1.65,
              }}
            >
              Las marcas en TNO no solo «suenan»: <strong>viven en tres dimensiones</strong> —impacto inmediato en vivo,
              longevidad del mensaje en catálogo y autoridad editorial con SEO.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: 'center',
                justifyContent: 'center',
                gap: { xs: 3, md: 2 },
                width: '100%',
                maxWidth: 1020,
                mx: 'auto',
              }}
            >
              {[
                {
                  Icon: LiveTvIcon,
                  title: 'Streaming en vivo',
                  line: 'Impacto inmediato, validación social y presencia en set con el talento.',
                },
                {
                  Icon: VideoLibraryIcon,
                  title: 'On-Demand',
                  line: 'Longevidad del mensaje: contenido siempre disponible que la audiencia elige ver con intención.',
                },
                {
                  Icon: MenuBookIcon,
                  title: 'Digest editorial',
                  line: 'Autoridad de marca en artículos especializados, profundidad y posicionamiento en buscadores.',
                },
              ].map(({ Icon, title, line }, i) => (
                <React.Fragment key={title}>
                  <Box
                    sx={{
                      flex: { md: '1 1 0' },
                      textAlign: 'center',
                      p: { xs: 2.5, md: 3 },
                      borderRadius: 2,
                      bgcolor: BG_PAPER,
                      border: '1px solid',
                      borderColor: (theme) => alpha(theme.palette.primary.main, 0.28),
                      width: { xs: '100%', md: 'auto' },
                      maxWidth: 320,
                    }}
                  >
                    <Icon sx={{ fontSize: 50, color: STRATEGIC_NEON_CYAN, mb: 1.5, filter: 'drop-shadow(0 0 10px rgba(34,211,238,0.25))' }} />
                    <Typography sx={{ fontWeight: 800, color: 'common.white', fontSize: '1.22rem', mb: 1 }}>{title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.84)', lineHeight: 1.55, fontSize: '1.03rem' }}>{line}</Typography>
                  </Box>
                  {i < 2 && (
                    <DeviceHubIcon
                      sx={{
                        color: (theme) => alpha(theme.palette.primary.main, 0.5),
                        fontSize: { xs: 30, md: 40 },
                        transform: { xs: 'rotate(90deg)', md: 'none' },
                      }}
                    />
                  )}
                </React.Fragment>
              ))}
            </Box>
          </Box>

          {/* Slide 3 · Métricas de impacto */}
          <Box sx={{ ...sectionShellSx, textAlign: 'center' }}>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1 }}>
              Métricas de impacto 2026
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.72rem', md: '2.35rem' }, color: 'common.white', mb: 1, maxWidth: 800, mx: 'auto' }}>
              Alcance que respalda la conversación con marcas
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.72)', mb: 3, maxWidth: 720, mx: 'auto', fontSize: '0.98rem' }}>
              Comunidad orgánica, lectura profunda y volumen de producción en la señal.
            </Typography>
            <Grid container spacing={2} sx={{ maxWidth: 1000, width: '100%', mx: 'auto' }}>
              {businessLevelMetrics.map((m) => (
                <Grid item xs={12} sm={6} md={3} key={m.label}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: BG_PAPER,
                      border: '1px solid rgba(255,255,255,0.08)',
                      borderTop: '4px solid',
                      borderTopColor: m.accent,
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.85rem', md: '2.1rem' }, color: 'common.white', mb: 1 }}>
                      {m.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.86)', lineHeight: 1.55, fontSize: '0.98rem' }}>{m.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
            <Typography sx={{ color: STRATEGIC_NEON_CYAN, fontWeight: 800, mt: 5, mb: 2, letterSpacing: 2 }} variant="overline">
              Distribución y engagement
            </Typography>
            <Grid container spacing={2} sx={{ maxWidth: 1000, width: '100%', mx: 'auto' }}>
              {impactAudienceMetrics.map((m) => (
                <Grid item xs={12} md={4} key={m.label}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(0,0,0,0.25)',
                      border: '1px solid rgba(34,211,238,0.22)',
                      borderTop: '4px solid',
                      borderTopColor: m.accent,
                    }}
                  >
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.75rem', md: '2rem' }, color: 'common.white', mb: 1 }}>
                      {m.value}
                    </Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.86)', lineHeight: 1.55, fontSize: '0.96rem' }}>{m.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Slide 6 · Valores agregados */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch', maxWidth: 1100, mx: 'auto', width: '100%' }}>
            <Typography variant="overline" sx={{ color: STRATEGIC_NEON_CYAN, letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: 'center' }}>
              Para su marca
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.45rem' }, color: 'common.white', mb: 3, textAlign: 'center' }}>
              Valores agregados
            </Typography>
            <Grid container spacing={2}>
              {tnoValueAdds.map((v) => (
                <Grid item xs={12} sm={6} key={v.title}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.25,
                      borderRadius: 2,
                      bgcolor: BG_PAPER,
                      border: '1px solid rgba(34,211,238,0.15)',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, color: STRATEGIC_NEON_CYAN, fontSize: '1.08rem', mb: 1 }}>{v.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.88)', lineHeight: 1.6, fontSize: '1.02rem' }}>{v.text}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Slide 7 · Roadmap de inversión */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch', maxWidth: 960, mx: 'auto', width: '100%' }}>
            <Timeline sx={{ fontSize: 44, color: 'primary.main', mb: 1, alignSelf: 'center' }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: 'center' }}>
              Inversión 2026
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.72rem', md: '2.35rem' }, color: 'common.white', mb: 3, textAlign: 'center' }}>
              Roadmap por fases
            </Typography>
            <Grid container spacing={2}>
              {investmentRoadmap2026.map((row) => (
                <Grid item xs={12} md={4} key={row.phase}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2.5,
                      borderRadius: 2,
                      bgcolor: 'rgba(0,0,0,0.2)',
                      border: '1px solid rgba(201,162,39,0.35)',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.95rem', mb: 0.75 }}>{row.phase}</Typography>
                    <Typography sx={{ fontWeight: 800, color: 'common.white', fontSize: '1.12rem', mb: 1 }}>{row.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.55, fontSize: '0.98rem' }}>{row.line}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Servicios comerciales acreditados */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch', maxWidth: 1080, mx: 'auto', width: '100%' }}>
            <VerifiedIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1, alignSelf: 'center' }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: 'center' }}>
              Portafolio 2026
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.72rem', md: '2.35rem' }, color: 'common.white', mb: 1, textAlign: 'center' }}>
              Servicios comerciales acreditados
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', maxWidth: 720, mb: 3, mx: 'auto', fontSize: '1.02rem' }}>
              Productos con contrato, entregables y narrativa alineada al ADN TNO Network —listos para brief de mercadeo.
            </Typography>
            <Grid container spacing={2}>
              {accreditedCommercialServices.map((s) => (
                <Grid item xs={12} sm={6} key={s.title}>
                  <Box
                    sx={{
                      height: '100%',
                      p: 2,
                      borderRadius: 1.5,
                      bgcolor: BG_PAPER,
                      border: '1px solid rgba(255,255,255,0.1)',
                    }}
                  >
                    <Typography sx={{ fontWeight: 800, color: 'common.white', fontSize: '1.05rem', mb: 0.75 }}>{s.title}</Typography>
                    <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.55, fontSize: '0.98rem' }}>{s.line}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Grilla por nichos: referencia sala + producciones del API (sin duplicar; detalle en web) */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch', maxWidth: 1180, mx: 'auto', width: '100%' }}>
            <ProgramGridByNiche variant="dark" />
          </Box>

          {/* Slide 5 · Aliados por categoría */}
          <Box sx={{ ...sectionShellSx, alignItems: 'stretch', maxWidth: 1000, mx: 'auto', width: '100%' }}>
            <Storefront sx={{ fontSize: 44, color: 'primary.main', mb: 1, alignSelf: 'center' }} />
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 1, alignSelf: 'center' }}>
              Confianza en el ecosistema
            </Typography>
            <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.72rem', md: '2.35rem' }, color: 'common.white', mb: 2, textAlign: 'center' }}>
              Casos y aliados
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.75)', textAlign: 'center', mb: 3, maxWidth: 720, mx: 'auto', fontSize: '0.98rem' }}>
              Marcas que ya integran narrativa con TNO.
            </Typography>
            <Box sx={{ width: '100%' }}>
              {strategicAlliesByCategory.map((block) => (
                <Box
                  key={block.category}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: BG_PAPER,
                    border: '1px solid rgba(255,255,255,0.1)',
                    mb: 2,
                  }}
                >
                  <Typography sx={{ fontWeight: 800, color: STRATEGIC_NEON_CYAN, fontSize: '0.92rem', mb: 1.25, textTransform: 'uppercase' }}>
                    {block.category}
                  </Typography>
                  <Grid container spacing={1.25}>
                    {block.brands.map((b) => {
                      const brand = typeof b === 'string' ? { name: b } : b;
                      const brandName = brand?.name || '';
                      const logoPath = brand?.logoPath || '';
                      const circleFill = brand?.circleFill || 'outline';
                      const filledWhite = circleFill === 'white';
                      const filledBlack = circleFill === 'black';
                      return (
                        <Grid item xs={12} sm={6} md={4} key={brandName}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1.6,
                              borderRadius: 1.25,
                              bgcolor: 'rgba(255,255,255,0.04)',
                              border: '1px solid rgba(255,255,255,0.08)',
                            }}
                          >
                            <Box
                              sx={{
                                width: 92,
                                height: 92,
                                minWidth: 92,
                                borderRadius: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: filledWhite ? '#ffffff' : filledBlack ? '#000000' : 'rgba(201,162,39,0.2)',
                                border: '1px solid',
                                borderColor: filledWhite
                                  ? 'rgba(255,255,255,0.95)'
                                  : filledBlack
                                    ? 'rgba(255,255,255,0.28)'
                                    : 'rgba(201,162,39,0.45)',
                                color: filledWhite ? '#111111' : 'common.white',
                                fontWeight: 900,
                                letterSpacing: 0.5,
                                fontSize: '0.82rem',
                                overflow: 'hidden',
                                p: filledWhite || filledBlack ? 1.2 : 0,
                              }}
                            >
                              {logoPath ? (
                                <Box
                                  component="img"
                                  src={imgSrc(logoPath)}
                                  alt={brandName}
                                  sx={{ display: 'block', width: '90%', height: '90%', objectFit: 'contain' }}
                                />
                              ) : (
                                brandMonogram(brandName)
                              )}
                            </Box>
                            <Typography sx={{ color: 'common.white', fontWeight: 700, fontSize: '1.22rem', lineHeight: 1.28 }}>{brandName}</Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              ))}
            </Box>
          </Box>

          {/* Cierre */}
          <Box
            sx={{
              ...sectionShellSx,
              textAlign: 'center',
              borderBottom: 'none',
              pb: { xs: 10, md: 12 },
            }}
          >
            <Typography
              component="p"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.35rem', md: '1.65rem' },
                color: STRATEGIC_NEON_CYAN,
                mb: 2,
                maxWidth: 720,
                mx: 'auto',
                lineHeight: 1.35,
                textShadow: '0 0 20px rgba(34,211,238,0.2)',
              }}
            >
              {strategicClosing.tagline}
            </Typography>
            <Typography variant="overline" sx={{ color: 'primary.main', letterSpacing: 4, fontWeight: 800, mb: 2 }}>
              Siguiente paso
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.82)', mb: 2, maxWidth: 640, mx: 'auto', fontSize: '0.98rem' }}>
              {strategicClosing.ctaNote}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.88)', mb: 2, maxWidth: 640, mx: 'auto' }}>
              Media kit público y registro <BrandTnoCon /> The Business Level.
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mb: 2, fontWeight: 700 }}>
              {strategicClosing.contactName}
              <Box component="span" sx={{ display: 'block', fontWeight: 500, fontSize: '0.95rem', color: 'rgba(255,255,255,0.75)', mt: 0.5 }}>
                {strategicClosing.contactRole}
              </Box>
            </Typography>
            <Typography component="div" sx={{ fontWeight: 800, fontSize: { xs: '1.2rem', md: '1.45rem' }, mb: 1.5 }}>
              <Link href={ADS_MEDIA_KIT_URL} target="_blank" rel="noopener noreferrer" underline="hover" sx={{ color: 'primary.main' }}>
                {ADS_MEDIA_KIT_URL}
              </Link>
            </Typography>
            <Link href={BUSINESS_LEVEL_REGISTER} underline="hover" sx={{ color: 'rgba(255,255,255,0.9)', fontWeight: 600 }}>
              Ir al formulario de acreditación corporativa
            </Link>
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default BusinessLevelPresentation;
